import React from 'react';
import './AdSense.css';

const ADSENSE_SCRIPT_ID = 'squadverse-adsense-script';
const ADSENSE_SCRIPT_TIMEOUT_MS = 5000;

let adSenseScriptPromise = null;
let adSenseScriptUnavailable = false;

const ADSENSE_CLIENT_ID = String(process.env.REACT_APP_ADSENSE_CLIENT_ID || '').trim();
const ADSENSE_ENABLED = process.env.REACT_APP_ADSENSE_ENABLED === 'true';
const ADSENSE_ACCOUNT_APPROVED = process.env.REACT_APP_ADSENSE_ACCOUNT_APPROVED === 'true';

export const ADSENSE_SLOTS = Object.freeze({
  homeFeatured: String(process.env.REACT_APP_ADSENSE_HOME_FEATURED_SLOT || '').trim(),
  homeFooter: String(process.env.REACT_APP_ADSENSE_HOME_FOOTER_SLOT || '').trim(),
  information: String(process.env.REACT_APP_ADSENSE_INFORMATION_SLOT || '').trim(),
  result: String(process.env.REACT_APP_ADSENSE_RESULT_SLOT || '').trim(),
});

// Keep manual ads on a substantial public content page. Home, game lobbies,
// setup flows, daily start screens and result/share views contain prominent
// navigation or controls where an adjacent ad could be mistaken for part of
// the experience.
const ADSENSE_CONTENT_ROUTES = new Set([
  '/tier-list',
]);

function isValidClientId(clientId) {
  return /^ca-pub-\d+$/i.test(clientId);
}

function isValidSlotId(slot) {
  return /^\d+$/.test(String(slot || '').trim());
}

export function isAdSenseConfigured({
  enabled = ADSENSE_ENABLED,
  accountApproved = ADSENSE_ACCOUNT_APPROVED,
  clientId = ADSENSE_CLIENT_ID,
} = {}) {
  return enabled && accountApproved && isValidClientId(clientId);
}

export function isAdSenseRuntimeAllowed({ nodeEnv, hostname, isPrerender = false } = {}) {
  const normalizedHost = String(hostname || '').toLowerCase();
  return nodeEnv === 'production'
    && !isPrerender
    && (normalizedHost === 'squadverse.uk' || normalizedHost === 'www.squadverse.uk');
}

export function isAdSenseContentRouteAllowed(pathname) {
  const rawPath = String(pathname || '/').split(/[?#]/, 1)[0] || '/';
  const normalizedPath = rawPath === '/' ? '/' : rawPath.replace(/\/+$/, '');
  return ADSENSE_CONTENT_ROUTES.has(normalizedPath);
}

function isLiveSiteHost() {
  if (typeof window === 'undefined') return false;
  return isAdSenseRuntimeAllowed({
    nodeEnv: process.env.NODE_ENV,
    hostname: window.location?.hostname,
    isPrerender: window.__SQUADVERSE_PRERENDER__ === true,
  });
}

function loadAdSenseScript() {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !isAdSenseConfigured()) {
    return Promise.resolve(false);
  }
  if (window.adsbygoogle) return Promise.resolve(true);
  if (adSenseScriptUnavailable) return Promise.resolve(false);
  if (adSenseScriptPromise) return adSenseScriptPromise;

  const existingScript = document.getElementById(ADSENSE_SCRIPT_ID);
  if (existingScript) {
    adSenseScriptPromise = new Promise((resolve) => {
      let settled = false;
      let timeoutId;
      const finish = (loaded) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        if (!loaded) adSenseScriptUnavailable = true;
        resolve(loaded);
      };
      existingScript.addEventListener('load', () => finish(Boolean(window.adsbygoogle)), { once: true });
      existingScript.addEventListener('error', () => finish(false), { once: true });
      timeoutId = window.setTimeout(() => finish(false), ADSENSE_SCRIPT_TIMEOUT_MS);
      if (window.adsbygoogle) finish(true);
    });
    return adSenseScriptPromise;
  }

  adSenseScriptPromise = new Promise((resolve) => {
    let settled = false;
    let timeoutId;
    const script = document.createElement('script');
    script.id = ADSENSE_SCRIPT_ID;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(ADSENSE_CLIENT_ID)}`;
    const finish = (loaded) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      if (loaded) {
        resolve(true);
        return;
      }
      adSenseScriptUnavailable = true;
      script.remove();
      resolve(false);
    };
    script.onload = () => finish(true);
    script.onerror = () => finish(false);
    timeoutId = window.setTimeout(() => finish(false), ADSENSE_SCRIPT_TIMEOUT_MS);
    document.head.appendChild(script);
  });

  return adSenseScriptPromise;
}

export default function AdSense({ slot, format = 'horizontal', className = '' }) {
  const adRef = React.useRef(null);
  const [status, setStatus] = React.useState('pending');
  const normalizedSlot = String(slot || '').trim();
  const canRender = isAdSenseConfigured()
    && process.env.NODE_ENV === 'production'
    && isLiveSiteHost()
    && isAdSenseContentRouteAllowed(window.location?.pathname)
    && isValidSlotId(normalizedSlot);

  React.useEffect(() => {
    if (!canRender || !adRef.current) return undefined;

    const adNode = adRef.current;
    let cancelled = false;
    let observer;

    const inspectAd = () => {
      if (cancelled) return;
      if (adNode.dataset.adStatus === 'unfilled') {
        setStatus('unfilled');
      } else if (adNode.querySelector('iframe')) {
        setStatus('filled');
      }
    };

    loadAdSenseScript().then((loaded) => {
      if (cancelled) return;
      if (!loaded || !window.adsbygoogle) {
        setStatus('unavailable');
        return;
      }

      try {
        window.adsbygoogle = window.adsbygoogle || [];
        if (adNode.dataset.squadverseRequested !== 'true') {
          window.adsbygoogle.push({});
          adNode.dataset.squadverseRequested = 'true';
        }
        observer = new MutationObserver(inspectAd);
        observer.observe(adNode, { attributes: true, childList: true, subtree: true });
        // Google owns the fill lifecycle. Only its explicit unfilled status
        // collapses the reserved wrapper; an arbitrary timer could hide a
        // valid creative while CMP consent or a slow network is resolving.
        inspectAd();
      } catch (_) {
        setStatus('unavailable');
      }
    });

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
    };
  }, [canRender, normalizedSlot]);

  if (!canRender || status === 'unfilled' || status === 'unavailable') return null;

  return (
    <aside
      className={`sv-ad ${status === 'filled' ? 'sv-ad--filled' : ''} ${className}`.trim()}
      data-squadverse-ad="true"
      data-html2canvas-ignore="true"
      aria-label="Advertisement"
    >
      <span className="sv-ad-label">Advertisement</span>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={normalizedSlot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </aside>
  );
}
